import {CheckCircleFillIcon, CheckCircleIcon, StopIcon} from '@primer/octicons-react'
import {Flash, IconButton} from '@primer/react'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {ConversationHeader} from './ConversationHeader'
import {useState} from 'react'

import styles from './ThreadHeader.module.css'
import {useResolveThreadMutation} from '../../mutations/use-resolve-thread-mutation'
import {useUnresolveThreadMutation} from '../../mutations/use-unresolve-thread-mutation'
import {fileLevelCommentKey} from '@github-ui/diff-lines/types'
import type {ThreadPreviewComp} from '../../page-data/payloads/thread-previews'

type ThreadHeaderProps = {
  firstCommentId?: number | null
  isCollapsed: boolean
  onNavigateToDiffComment: () => void
  onToggleCollapsed: () => void
  threadPreview: ThreadPreviewComp
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export function ThreadHeader({
  firstCommentId,
  isCollapsed,
  onToggleCollapsed,
  onNavigateToDiffComment,
  threadPreview,
  headingLevel,
}: ThreadHeaderProps) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const thread = threadPreview.thread
  const previewInfo = threadPreview.previewInfo

  const {mutate: resolveThreadMutation} = useResolveThreadMutation(
    previewInfo.path,
    thread?.line ? `R${thread?.line}` : fileLevelCommentKey,
  )
  const handleResolveThread = () => {
    if (thread?.isResolved) return

    resolveThreadMutation(
      {threadId: previewInfo.threadId},
      {
        onError: () => setErrorMessage('Failed to resolve thread'),
        onSuccess: () => {
          if (!isCollapsed) onToggleCollapsed()
        },
      },
    )
  }

  const {mutate: unresolveThreadMutation} = useUnresolveThreadMutation(
    previewInfo.path,
    thread?.line ? `L${thread?.line}` : fileLevelCommentKey,
  )
  const handleUnresolveThread = async () => {
    if (!threadPreview) return
    if (!thread?.isResolved) return

    unresolveThreadMutation(
      {threadId: threadPreview.previewInfo.threadId},
      {
        onError: () => setErrorMessage('Failed to unresolve thread'),
        onSuccess: () => {
          if (isCollapsed) onToggleCollapsed()
        },
      },
    )

    sendPullRequestAnalyticsEvent('comments.unresolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  return (
    <>
      <ConversationHeader
        firstCommentId={firstCommentId}
        isCollapsed={isCollapsed}
        isOutdated={thread?.isOutdated || false}
        isResolved={thread?.isResolved || false}
        line={thread?.line}
        path={previewInfo.path}
        rightSideContent={
          <IconButton
            aria-label={thread?.isResolved ? 'Unresolve conversation' : 'Resolve conversation'}
            tooltipDirection="sw"
            icon={thread?.isResolved ? CheckCircleFillIcon : CheckCircleIcon}
            // need to be specific in order to override default IconButton color
            data-thread-resolved={thread?.isResolved}
            className={styles.ThreadHeaderIconButton}
            variant="invisible"
            onClick={thread?.isResolved ? handleUnresolveThread : handleResolveThread}
          />
        }
        onNavigateToDiffComment={onNavigateToDiffComment}
        onToggleCollapsed={onToggleCollapsed}
        threadId={previewInfo.threadId}
        headingLevel={headingLevel}
      />
      {errorMessage && (
        <Flash variant="danger" className="m-2">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
    </>
  )
}

try{ ThreadHeader.displayName ||= 'ThreadHeader' } catch {}